/**
 * 1.4.0 asset groups, stream G2's data hooks: assigning a whole group for delivery (POST /api/v1/assignments with a
 * groupId; GroupAssignmentCreate → GroupAssignmentResult). The group types, groups and members hooks are in
 * ./groups.ts. Keys: queryKeys.groupAssignmentPreview() (under assignments()). G2's file.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DeliveryItemTemplate, GroupAssignmentCreate, GroupAssignmentResult } from '@prism/contracts';
import { api } from '../client';
import { queryKeys } from '../queryKeys';

export type GroupAssignmentBody = Partial<GroupAssignmentCreate> & Pick<GroupAssignmentCreate, 'groupId' | 'companyId'> & {
  timeZone?: string | null;
  contactUserId?: string | null;
};

/** The settings of the group's latest group assignment (the API answers them with a preview). */
export interface GroupAssignmentPrevious {
  companyId: string;
  portalUserId: string | null;
  specId: string | null;
  dueAt: string | null;
  timeZone: string | null;
  notes: string;
  contactUserId: string | null;
  items: DeliveryItemTemplate[];
}
export type GroupAssignmentAnswer = GroupAssignmentResult & { title: string; previous?: GroupAssignmentPrevious | null };

/** The preview makes nothing, so it needs no real company: the API checks the parties only when assigning. */
const NO_COMPANY = '00000000-0000-4000-8000-000000000000';

/** What assigning the group would do: the counts, each member's outcome and the previous settings. */
export function useGroupAssignmentPreview(groupId: string | null | undefined, onlyUnassigned: boolean, enabled = true) {
  return useQuery({
    queryKey: queryKeys.groupAssignmentPreview(`${groupId}:${onlyUnassigned ? 'unassigned' : 'all'}`),
    queryFn: () => api<GroupAssignmentAnswer>('/api/v1/assignments', { method: 'POST', json: { groupId, companyId: NO_COMPANY, preview: true, onlyUnassigned } }),
    enabled: enabled && Boolean(groupId),
    staleTime: 5_000,
  });
}

/** Preview (preview: true, nothing made) or assign. */
export function useGroupAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: GroupAssignmentBody) => api<GroupAssignmentAnswer>('/api/v1/assignments', { method: 'POST', json: body }),
    onSuccess: (r) => {
      if (r.preview) return;
      void qc.invalidateQueries({ queryKey: queryKeys.assignments() });
      void qc.invalidateQueries({ queryKey: queryKeys.groups() });
    },
  });
}
